import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Lightbulb, Eye, Shield, Leaf } from 'lucide-react';
import { aboutData } from '../data/mock';

const iconMap = {
  Lightbulb, Eye, Shield, Leaf
};

const About = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-teal-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">About Niloman HX</h1>
          <p className="text-xl text-cyan-50 max-w-3xl mx-auto">
            Pioneering smart water purification solutions for a healthier India
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">Our Story</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                {aboutData.story}
              </p>
            </div>
            <div className="relative h-[400px] rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1521453204431-f4d478004ce1"
                alt="Our Story"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
            </div>
          </div>

          {/* Mission & Vision */}
          <div className="grid md:grid-cols-2 gap-8 mb-20">
            <Card className="border-2 border-cyan-200 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900">Our Mission</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 leading-relaxed">
                  {aboutData.mission}
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-teal-200 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900">Our Vision</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 leading-relaxed">
                  {aboutData.vision}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Values */}
          <div>
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Our Core Values
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {aboutData.values.map((value, index) => {
                const Icon = iconMap[value.icon];
                return (
                  <Card 
                    key={index}
                    className="text-center border-2 hover:border-cyan-400 transition-all duration-300 hover:shadow-lg group"
                  >
                    <CardHeader>
                      <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                        <Icon className="h-8 w-8 text-white" />
                      </div>
                      <CardTitle className="text-xl">{value.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600">
                        {value.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Built by Experts, For Everyone
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our team combines expertise in IoT technology, water purification, and customer service to deliver exceptional smart RO solutions
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center border-2 hover:border-cyan-400 transition-all duration-300">
              <CardHeader>
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl font-bold text-cyan-600">10+</span>
                </div>
                <CardTitle>Years Combined Experience</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  In IoT development and water purification technology
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-2 hover:border-cyan-400 transition-all duration-300">
              <CardHeader>
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl font-bold text-cyan-600">24/7</span>
                </div>
                <CardTitle>Customer Support</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Dedicated support team always ready to help
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-2 hover:border-cyan-400 transition-all duration-300">
              <CardHeader>
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl font-bold text-cyan-600">500+</span>
                </div>
                <CardTitle>Happy Customers</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Across India trusting us for pure water
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-cyan-50 to-teal-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-2xl p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Why Choose Niloman HX?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {[
                "IoT technology developed by experts",
                "Made in India with global standards",
                "Food-grade, certified components",
                "Real-time monitoring & alerts",
                "Predictive maintenance technology",
                "Comprehensive warranty coverage",
                "Fast installation & service",
                "Transparent pricing, no hidden costs"
              ].map((reason, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <span className="text-gray-700 font-medium">{reason}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Join the Smart Water Revolution
          </h2>
          <p className="text-xl mb-8 text-cyan-50">
            Experience the difference that intelligent water purification can make
          </p>
          <button 
            onClick={() => window.location.href = '/contact'}
            className="bg-white text-cyan-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            Get Started Today
          </button>
        </div>
      </section>
    </div>
  );
};

export default About;
